import React from 'react';
import { ArrowRight, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SyncStatus from '../components/SyncStatus';
interface AlertData {
  id: number;
  name: string;
  description: string;
  category: string;
  matches: number;
  lastRun: string;
  status: string;
}
const Home: React.FC = () => {
  const recentAlerts: AlertData[] = [{
    id: 1,
    name: 'Potential Analytics Upsell (High Manual Reporting)',
    description: 'Flags accounts doing heavy manual analysis, suggesting they\'re ready for the "Advanced Analytics" package.',
    category: 'Upsell & Expansion',
    matches: 5,
    lastRun: '2 hours ago',
    status: 'Active'
  }, {
    id: 2,
    name: 'Potential Brand Portal Upsell (Brand Filter Usage)',
    description: 'Identifies accounts providing reports to external partners, signaling a need for "Brand Portals".',
    category: 'Upsell & Expansion',
    matches: 8,
    lastRun: '1 day ago',
    status: 'Active'
  }, {
    id: 4,
    name: 'Manufacturer Not Using Assemblies',
    description: 'A critical alert for a key persona not using a sticky feature, indicating a major onboarding failure or churn risk.',
    category: 'Churn & Onboarding Risk',
    matches: 7,
    lastRun: '6 hours ago',
    status: 'Active'
  }, {
    id: 5,
    name: 'High-Value Account is Underusing the Platform',
    description: 'Flags high-MRR customers who are dangerously disengaged from the product, requiring immediate proactive outreach to prevent churn.',
    category: 'Churn & Onboarding Risk',
    matches: 2,
    lastRun: '1 day ago',
    status: 'Active'
  }];
  const generateBarData = (matches: number) => {
    const days = 30;
    const data = [];
    for (let i = 0; i < days; i++) {
      const value = Math.floor(Math.random() * (matches + 1));
      data.push(value);
    }
    return data;
  };
  const getCategoryColor = (category: string, alertName: string) => {
    // Special case for "Bursting at the Seams Alert"
    if (alertName === 'Bursting at the Seams Alert') {
      return 'bg-yellow-500';
    }
    if (category.startsWith('Upsell')) {
      return 'bg-green-500';
    } else if (category.startsWith('Risk')) {
      return 'bg-red-500';
    } else if (category.startsWith('Action')) {
      return 'bg-yellow-500';
    } else {
      return 'bg-blue-500';
    }
  };
  const renderBarChart = (alert: AlertData) => {
    const barData = generateBarData(alert.matches);
    const maxValue = Math.max(...barData) || 1;
    const color = getCategoryColor(alert.category, alert.name);
    return <div className="flex items-end h-12 gap-[2px] mt-4 mb-2" data-id="element-882">
        {barData.map((value, index) => {
        const height = value ? value / maxValue * 100 : 0;
        return <div key={index} className="flex-1" data-id="element-883">
              <div className={`${color} rounded-t`} style={{
            height: `${height}%`,
            minHeight: value > 0 ? '2px' : '0'
          }} data-id="element-884"></div>
            </div>;
      })}
      </div>;
  };
  return <div className="space-y-6" data-id="element-885">
      <div className="flex justify-between items-center" data-id="element-886">
        <h1 className="text-xl font-bold text-gray-900" data-id="element-887">Recent Alerts</h1>
        <Link to="/alerts" className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-888">
          View All Alerts
          <ArrowRight size={16} className="ml-1" data-id="element-889" />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-4" data-id="element-890">
        {recentAlerts.map(alert => <div key={alert.id} className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-id="element-891">
            <div className="p-4" data-id="element-892">
              <div className="flex items-center justify-between mb-2" data-id="element-893">
                <div className="flex items-center gap-2" data-id="element-894">
                  <div className="p-1 rounded-full bg-gray-100" data-id="element-895">
                    <AlertTriangle size={14} className={`${getCategoryColor(alert.category, alert.name).replace('bg-', 'text-')}`} data-id="element-896" />
                  </div>
                  <h3 className="text-sm font-medium" data-id="element-897">{`\'${alert.name}\'`}</h3>
                </div>
                <span className="text-xs text-gray-500" data-id="element-898">{alert.lastRun}</span>
              </div>
              <p className="text-xs text-gray-500 mb-2" data-id="element-899">{alert.description}</p>
              {renderBarChart(alert)}
              <div className="flex justify-between items-center mt-3 text-xs" data-id="element-900">
                <span className="text-gray-500" data-id="element-901">Last 30 days</span>
                <span className="font-medium" data-id="element-902">{alert.matches} matches</span>
              </div>
            </div>
          </div>)}
      </div>
      <div className="flex justify-between items-center mt-8" data-id="element-903">
        <h2 className="text-xl font-medium text-gray-900" data-id="element-904">Accounts Overview</h2>
        <Link to="/accounts" className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-905">
          View All Accounts
          <ArrowRight size={16} className="ml-1" data-id="element-906" />
        </Link>
      </div>
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden" data-id="element-907">
        <table className="w-full" data-id="element-908">
          <thead data-id="element-909">
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200" data-id="element-910">
              <th className="px-4 py-3 font-medium" data-id="element-911">Company Name</th>
              <th className="px-4 py-3 font-medium" data-id="element-912">Owner</th>
              <th className="px-4 py-3 font-medium" data-id="element-913">ARR</th>
              <th className="px-4 py-3 font-medium" data-id="element-914">Renewal Date</th>
              <th className="px-4 py-3 font-medium" data-id="element-915">Last Seen</th>
              <th className="px-4 py-3 font-medium" data-id="element-916">HubSpot Sync</th>
            </tr>
          </thead>
          <tbody data-id="element-917">
            <tr className="border-b border-gray-100" data-id="element-918">
              <td className="px-4 py-3 text-sm" data-id="element-919">Initech</td>
              <td className="px-4 py-3 text-sm" data-id="element-920">John Smith</td>
              <td className="px-4 py-3 text-sm" data-id="element-921">$75,000</td>
              <td className="px-4 py-3 text-sm" data-id="element-922">Dec 14, 2023</td>
              <td className="px-4 py-3 text-sm" data-id="element-923">May 9, 2023</td>
              <td className="px-4 py-3" data-id="element-924">
                <SyncStatus status="Synced" data-id="element-925" />
              </td>
            </tr>
            <tr className="border-b border-gray-100" data-id="element-926">
              <td className="px-4 py-3 text-sm" data-id="element-927">Acme Corp</td>
              <td className="px-4 py-3 text-sm" data-id="element-928">Sarah Johnson</td>
              <td className="px-4 py-3 text-sm" data-id="element-929">$120,000</td>
              <td className="px-4 py-3 text-sm" data-id="element-930">Sep 30, 2023</td>
              <td className="px-4 py-3 text-sm" data-id="element-931">May 8, 2023</td>
              <td className="px-4 py-3" data-id="element-932">
                <SyncStatus status="Synced" data-id="element-933" />
              </td>
            </tr>
            <tr className="border-b border-gray-100" data-id="element-934">
              <td className="px-4 py-3 text-sm" data-id="element-935">Globex</td>
              <td className="px-4 py-3 text-sm" data-id="element-936">Michael Brown</td>
              <td className="px-4 py-3 text-sm" data-id="element-937">$95,000</td>
              <td className="px-4 py-3 text-sm" data-id="element-938">Nov 19, 2023</td>
              <td className="px-4 py-3 text-sm" data-id="element-939">May 7, 2023</td>
              <td className="px-4 py-3" data-id="element-940">
                <SyncStatus status="Synced" data-id="element-941" />
              </td>
            </tr>
            <tr data-id="element-942">
              <td className="px-4 py-3 text-sm" data-id="element-943">Umbrella Corp</td>
              <td className="px-4 py-3 text-sm" data-id="element-944">Emily Davis</td>
              <td className="px-4 py-3 text-sm" data-id="element-945">$200,000</td>
              <td className="px-4 py-3 text-sm" data-id="element-946">Sep 14, 2023</td>
              <td className="px-4 py-3 text-sm" data-id="element-947">Apr 30, 2023</td>
              <td className="px-4 py-3" data-id="element-948">
                <SyncStatus status="Not Synced" data-id="element-949" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>;
};
export default Home;